/**
 * Banque des sujets d'examens des années antérieures — portail étudiant.
 * Filtres : matière, niveau, année académique, option.
 */
import { useEffect, useState, useMemo } from 'react';
import {
  BookOpen, Loader2, Search, Download, FileText, Calendar, ChevronDown, X,
  Share2, Check,
} from 'lucide-react';
import { PortailLayout } from '../PortailLayout';
import { portailApi } from '../services/portailApi';

interface Sujet {
  id: number;
  titre: string;
  description: string;
  matiere_libelle: string;
  matiere_code: string;
  niveau: string;
  option: string;
  annee_academique: string;
  semestre: string;
  session: 'normale' | 'rattrapage';
  fichier_url: string | null;
  taille_octets: number;
  date_creation: string;
  nb_telechargements: number;
}

function humanSize(n: number): string {
  if (!n) return '';
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
  return `${(n / 1024 / 1024).toFixed(1)} Mo`;
}

export default function BanqueSujets() {
  const [sujets, setSujets] = useState<Sujet[]>([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState('');
  const [search, setSearch] = useState('');
  const [annee, setAnnee] = useState('');
  const [annees, setAnnees] = useState<string[]>([]);

  const load = async () => {
    setLoading(true); setErreur('');
    try {
      const r = await portailApi.getSujetsExamens({
        annee_academique: annee || undefined,
      });
      setSujets(r.results || []);
      setAnnees(r.filtres_dispo?.annees || []);
    } catch (e: any) {
      setErreur(e?.message || 'Impossible de charger les sujets.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [annee]);

  const filtres = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sujets;
    return sujets.filter(s =>
      s.titre.toLowerCase().includes(q)
      || s.matiere_libelle.toLowerCase().includes(q)
      || s.matiere_code.toLowerCase().includes(q)
    );
  }, [sujets, search]);

  // Groupage par matière
  const parMatiere = useMemo(() => {
    const map: Record<string, Sujet[]> = {};
    for (const s of filtres) {
      (map[s.matiere_libelle] ||= []).push(s);
    }
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [filtres]);

  const handleTelecharger = async (s: Sujet) => {
    try {
      const r = await portailApi.telechargerSujetExamen(s.id);
      if (r.fichier_url) {
        window.open(r.fichier_url, '_blank');
      }
    } catch {
      if (s.fichier_url) window.open(s.fichier_url, '_blank');
    }
  };

  const [copieId, setCopieId] = useState<number | null>(null);
  const handlePartager = async (s: Sujet) => {
    if (!s.fichier_url) return;
    const titre = `Sujet d'examen : ${s.matiere_libelle} (${s.annee_academique})`;
    const texte = `${titre}${s.session === 'rattrapage' ? ' — Rattrapage' : ''}`;
    // Web Share API (Android/iOS supportés)
    if (navigator.share) {
      try {
        await navigator.share({ title: titre, text: texte, url: s.fichier_url });
        return;
      } catch {
        // utilisateur a annulé → on tente le fallback
      }
    }
    // Fallback : copier le lien dans le presse-papier
    try {
      await navigator.clipboard.writeText(`${texte}\n${s.fichier_url}`);
      setCopieId(s.id);
      setTimeout(() => setCopieId(null), 1500);
    } catch {
      alert(`Lien à partager :\n${s.fichier_url}`);
    }
  };

  return (
    <PortailLayout title="Banque des sujets d'examens">
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-3 mb-3 space-y-2">
        <div className="relative">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Matière, titre…"
            className="w-full pl-7 pr-7 py-1.5 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded text-slate-900 dark:text-slate-100" />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400">
              <X size={12} />
            </button>
          )}
        </div>
        <div className="relative">
          <select value={annee} onChange={(e) => setAnnee(e.target.value)}
            className="w-full appearance-none px-2 py-1.5 pr-7 text-xs bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded text-slate-900 dark:text-slate-100">
            <option value="">Toutes années</option>
            {annees.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        </div>
      </div>

      {loading && (
        <div className="text-center py-6 text-slate-500 text-sm">
          <Loader2 size={14} className="inline animate-spin mr-2" /> Chargement…
        </div>
      )}
      {erreur && (
        <div className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded p-2 text-sm">
          {erreur}
        </div>
      )}

      {!loading && parMatiere.length === 0 && (
        <div className="text-center py-8 text-slate-500 text-sm">
          <BookOpen size={32} className="inline mb-2 opacity-50" />
          <div>Aucun sujet disponible pour ces critères.</div>
        </div>
      )}

      <div className="space-y-3">
        {parMatiere.map(([matiere, list]) => (
          <div key={matiere}>
            <h3 className="text-[11px] uppercase tracking-wider font-bold text-slate-600 dark:text-slate-400 mb-1.5 px-1 inline-flex items-center gap-1.5">
              <BookOpen size={11} /> {matiere}
              <span className="text-[10px] font-normal text-slate-400">({list.length})</span>
            </h3>
            <div className="space-y-1.5">
              {list.map(s => (
                <article key={s.id}
                  className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-2.5 flex items-start gap-2">
                  <div className="w-9 h-9 rounded bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center flex-shrink-0">
                    <FileText size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                      {s.titre}
                    </div>
                    <div className="text-[10px] text-slate-500 truncate flex flex-wrap gap-1.5 mt-0.5">
                      <span className="px-1 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                        {s.niveau}
                      </span>
                      <span className="px-1 py-0.5 bg-slate-100 dark:bg-slate-700 rounded">
                        <Calendar size={9} className="inline mr-0.5" /> {s.annee_academique}
                      </span>
                      {s.session === 'rattrapage' && (
                        <span className="px-1 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded">
                          Rattrapage
                        </span>
                      )}
                      {s.semestre && (
                        <span className="px-1 py-0.5 bg-slate-100 dark:bg-slate-700 rounded">{s.semestre}</span>
                      )}
                      {humanSize(s.taille_octets) && (
                        <span className="text-slate-400">{humanSize(s.taille_octets)}</span>
                      )}
                    </div>
                    {s.description && (
                      <div className="text-[10px] text-slate-500 mt-1 line-clamp-2">{s.description}</div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <button onClick={() => handleTelecharger(s)}
                      className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded inline-flex items-center justify-center"
                      title="Télécharger / Consulter">
                      <Download size={14} />
                    </button>
                    <button onClick={() => handlePartager(s)}
                      className="p-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded inline-flex items-center justify-center relative"
                      title="Partager le lien (WhatsApp, mail…)">
                      {copieId === s.id ? <Check size={14} /> : <Share2 size={14} />}
                      {copieId === s.id && (
                        <span className="absolute -top-7 right-0 text-[9px] bg-emerald-900 text-emerald-100 px-2 py-0.5 rounded whitespace-nowrap">
                          Lien copié
                        </span>
                      )}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        ))}
      </div>
    </PortailLayout>
  );
}
